import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getTopics, getClusteringStatus, regenerateTopics } from '../services/topics';
import { setTitle } from '../utils/environment';
import { formatDateCompact } from '../utils/dateUtils';
import { TopicLink } from '../components/SmartLink';
import PageHeader from '../components/PageHeader';
import { useConfirm } from '../hooks/useConfirm';

const Topics = () => {
  const confirm = useConfirm();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenerateMessage, setRegenerateMessage] = useState(null);
  const pollingRef = useRef(null);

  const fetchTopics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getTopics({
        limit: 50,
        unread_only: true
      });
      setTopics(response.topics || []);
    } catch (err) {
      console.error('Failed to fetch topics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Check clustering status and start polling if running
  const checkClusteringStatus = useCallback(async () => {
    try {
      const status = await getClusteringStatus();
      setIsRegenerating(status.is_running);

      if (status.is_running && !pollingRef.current) {
        // Start polling
        pollingRef.current = setInterval(async () => {
          try {
            const newStatus = await getClusteringStatus();
            if (!newStatus.is_running) {
              // Clustering finished, stop polling and refresh
              clearInterval(pollingRef.current);
              pollingRef.current = null;
              setIsRegenerating(false);
              setRegenerateMessage('Topics regenerated successfully!');
              setTimeout(() => setRegenerateMessage(null), 3000);
              await fetchTopics();
            }
          } catch (pollErr) {
            console.error('Error polling clustering status:', pollErr);
          }
        }, 2000);
      }
    } catch (err) {
      console.error('Error checking clustering status:', err);
    }
  }, [fetchTopics]);

  // Handle regeneration request
  const handleRegenerate = async () => {
    if (!(await confirm({
      emoji: '🔄',
      title: 'Regenerate Topics?',
      message: 'This will delete all existing topics and create new ones based on your current articles. This process may take a minute or two.',
      confirmLabel: 'Regenerate',
    }))) return;
    try {
      const response = await regenerateTopics();
      if (response.success) {
        setIsRegenerating(true);
        setRegenerateMessage('Regenerating topics...');
        // Start polling for completion
        checkClusteringStatus();
      } else {
        setRegenerateMessage(response.error || 'Failed to start regeneration');
        setTimeout(() => setRegenerateMessage(null), 5000);
      }
    } catch (err) {
      console.error('Failed to regenerate topics:', err);
      setRegenerateMessage(err.message);
      setTimeout(() => setRegenerateMessage(null), 5000);
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);


  const formatScore = (score) => {
    return Math.round(score * 100);
  };

  const getScoreColor = (score) => {
    if (score >= 0.8) return 'text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-900/40 border-fun-mint';
    if (score >= 0.6) return 'text-primary-700 dark:text-primary-300 bg-primary-100 dark:bg-primary-900/40 border-primary-400';
    if (score >= 0.4) return 'text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/40 border-fun-lemon';
    return 'text-secondary-text bg-surface-hover border-theme-border-subtle';
  };

  const formatRecency = (days) => {
    if (days < 1) return 'today';
    if (days < 2) return '1 day ago';
    if (days < 7) return `${Math.round(days)} days ago`;
    if (days < 30) return `${Math.round(days / 7)} weeks ago`;
    return `${Math.round(days / 30)} months ago`;
  };

  useEffect(() => {
    setTitle('Topics · Read It Never');
    fetchTopics();
    checkClusteringStatus();
  }, [fetchTopics, checkClusteringStatus]);

  return (
    <div className="min-h-screen bg-clay-bg">
      {/* Status Message */}
      {regenerateMessage && (
        <div className={`fixed top-4 right-4 z-40 shadow-clay ${
          regenerateMessage.includes('success') ? 'notice-clay-success' :
          regenerateMessage.includes('Regenerating') ? 'notice-clay-info' :
          'notice-clay-error'
        }`}>
          <div className="flex items-center">
            {isRegenerating && (
              <div className="spinner-fun-sm w-4 h-4 mr-2"></div>
            )}
            <span className="font-medium">{regenerateMessage}</span>
          </div>
        </div>
      )}

      <PageHeader
        emoji="📊"
        title="Topics"
        subtitle={`${topics.length} AI-generated topic clusters`}
        backTo="/dashboard"
        backLabel="Back to dashboard"
        actions={
          <button
            onClick={handleRegenerate}
            disabled={isRegenerating}
            className="btn-clay-icon"
            aria-label="Regenerate topics"
            title={isRegenerating ? 'Regenerating...' : 'Regenerate topics'}
          >
            <svg
              className={`w-5 h-5 ${isRegenerating ? 'animate-spin' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
        }
      />

      {/* Content */}
      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-8">

        {/* Error Message */}
        {error && (
          <div className="notice-clay-error flex items-center gap-2 mb-6">
            <span className="text-xl" role="img" aria-hidden="true">😅</span>
            <span className="font-medium">{error}</span>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="spinner-fun-lg mx-auto mb-4"></div>
              <p className="font-display text-lg text-secondary-text">Loading topics...</p>
            </div>
          </div>
        ) : topics.length === 0 ? (
          /* Empty State */
          <div className="card-clay-static p-12 text-center">
            <div className="text-6xl mb-4 animate-bounce-gentle">📊</div>
            <h2 className="font-display text-lg font-semibold text-heading mb-2">No topics found</h2>
            <p className="text-secondary-text max-w-md mx-auto">
              Topics are automatically generated when you have unread articles. Check back later or visit your dashboard to see if new topics have been created.
            </p>
          </div>
        ) : (
          /* Topics Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {topics.map((topic, index) => (
              <TopicLink
                key={topic.id}
                topicId={topic.id}
                context={{ source: 'topics' }}
                className="card-clay p-3 sm:p-6 cursor-pointer border-l-4 border-primary-500
                  hover:border-l-accent-500 block no-underline"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="flex items-start justify-between gap-2 mb-3">
                  <h3 className="font-display text-lg font-semibold text-heading leading-6 min-w-0">
                    {topic.label}
                  </h3>
                  <div className={`px-3 py-1 rounded-clay text-sm font-bold border-2 flex-shrink-0 ${getScoreColor(topic.final_score)}`}>
                    {formatScore(topic.final_score)}%
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-4 text-sm text-secondary-text">
                  <span className="badge-fun-sky text-xs px-2 py-0.5">{topic.unread_count} unread</span>
                  <span className="badge-fun-mint text-xs px-2 py-0.5">{topic.cluster_size} total</span>
                </div>

                <div className="text-sm text-secondary-text space-y-1">
                  <div className="flex items-center justify-between">
                    <span>Average age:</span>
                    <span className="font-medium">{formatRecency(topic.avg_recency_days)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Created:</span>
                    <span className="font-medium">{formatDateCompact(topic.created_at)}</span>
                  </div>
                  {topic.avg_feedback_score !== 0 && (
                    <div className="flex items-center justify-between">
                      <span>Avg feedback:</span>
                      <span className="font-medium">
                        {topic.avg_feedback_score > 0 ? '👍' : '👎'}
                        {Math.abs(topic.avg_feedback_score).toFixed(1)}
                      </span>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-3 border-t-2 border-clay-border">
                  <div className="text-xs text-tertiary-text flex flex-wrap items-center justify-between gap-1">
                    <span className="badge-fun-lavender text-xs px-2 py-0.5">{topic.algorithm}</span>
                    {topic.topic_metadata?.silhouette_score && (
                      <span>Quality: {(topic.topic_metadata.silhouette_score * 100).toFixed(1)}%</span>
                    )}
                  </div>
                </div>
              </TopicLink>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Topics;